<script setup lang="ts">
import { PrismEditor } from 'vue-prism-editor'
import { highlight, languages } from 'prismjs/components/prism-core'

import 'prismjs/components/prism-markup'
import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-javascript'
import 'vue-prism-editor/dist/prismeditor.min.css'
import 'prismjs/themes/prism.css'

/**
 * An editable code field — a prism-highlighted textarea, `v-model` on the string.
 *
 * `CodeArea` is the read-only half of the pair, for a block the user is meant to copy out;
 * this one is for code they write. It defaults to markup with embedded JavaScript;
 * use `language="javascript"` for a plain script. It brings no label or validation state,
 * so put it in a `FormItem`.
 */
const props = withDefaults(
  defineProps<{
    language?: 'markup' | 'javascript'
    lineNumbers?: boolean
    readonly?: boolean
    placeholder?: string
  }>(),
  { language: 'markup', lineNumbers: true, readonly: false, placeholder: '' }
)

const code = defineModel<string>({ default: '' })

function highlighter(value: string) {
  return highlight(value, languages[props.language], props.language)
}
</script>

<template>
  <PrismEditor
    v-model="code"
    class="code-editor"
    :highlight="highlighter"
    :line-numbers="lineNumbers"
    :readonly="readonly"
    :placeholder="placeholder"
  />
</template>

<style lang="scss" scoped>
.code-editor {
  /* el-form-item__content is a flex row — take the full width to fill it */
  width: 100%;
  min-height: 150px;
  max-height: 300px;
  padding: 8px;
  font-family: 'Fira Code', 'Fira Mono', Consolas, Menlo, Courier, monospace;
  font-size: 13px;
  line-height: 20px;
  color: var(--el-text-color-primary);
  background: var(--el-fill-color-light);
  border: 1px solid var(--el-border-color);
  border-radius: var(--el-border-radius-base);
}

:deep(.prism-editor__textarea:focus) {
  outline: none;
}

:deep(.prism-editor__line-numbers) {
  color: var(--el-text-color-placeholder);
}
</style>
